import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEO from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import SafeIcon from '../components/common/SafeIcon';
import PokemonGrid from '../components/pokemon/PokemonGrid';
import TypeBadge from '../components/pokemon/TypeBadge';
import { searchPokemon, pokemonTypes, getAllAbilities } from '../data/pokemonData';

const { FiSearch, FiFilter, FiX } = FiIcons;

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  
  // Initialize state from URL to ensure consistency on first render
  const getInitialParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      q: params.get('q') || '',
      type: params.get('type') || '',
      ability: params.get('ability') || '',
      minStat: params.get('minStat') || '',
      maxStat: params.get('maxStat') || '',
      statType: params.get('statType') || 'hp'
    };
  };

  const initialParams = getInitialParams();

  // Local state for UI inputs
  const [query, setQuery] = useState(initialParams.q);
  const [filters, setFilters] = useState({
    type: initialParams.type,
    ability: initialParams.ability,
    minStat: initialParams.minStat,
    maxStat: initialParams.maxStat,
    statType: initialParams.statType
  });
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // 1. Sync State -> URL
  // When user interacts with UI, update the URL. 
  // This is the primary driver for changes initiated from this page.
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (filters.type) params.set('type', filters.type);
    if (filters.ability) params.set('ability', filters.ability);
    if (filters.minStat) params.set('minStat', filters.minStat);
    if (filters.maxStat) params.set('maxStat', filters.maxStat);
    if (filters.statType !== 'hp') params.set('statType', filters.statType);
    
    // Convert to string for comparison to avoid redundant updates
    const currentStr = searchParams.toString();
    const newStr = params.toString();
    
    if (currentStr !== newStr) {
      setSearchParams(params, { replace: true });
    }
  }, [query, filters, searchParams, setSearchParams]);

  // 2. Sync URL -> State & Perform Search
  // This handles:
  // - Initial load
  // - Browser Back/Forward buttons
  // - Navigation from other components (e.g., Header search)
  // - Updates triggered by Effect 1 (above)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    const urlQuery = params.get('q') || '';
    const urlFilters = {
      type: params.get('type') || '',
      ability: params.get('ability') || '',
      minStat: params.get('minStat') || '',
      maxStat: params.get('maxStat') || '',
      statType: params.get('statType') || 'hp'
    };

    // Sync URL params back to local state if they differ
    // This ensures inputs reflect the URL when navigating via history or header
    if (urlQuery !== query) {
      setQuery(urlQuery);
    }
    
    // Check if any filter differs
    const filtersChanged = 
      urlFilters.type !== filters.type ||
      urlFilters.ability !== filters.ability ||
      urlFilters.minStat !== filters.minStat ||
      urlFilters.maxStat !== filters.maxStat ||
      urlFilters.statType !== filters.statType;

    if (filtersChanged) {
      setFilters(urlFilters);
    }

    // Always perform search based on URL parameters (Source of Truth)
    performSearch(urlQuery, urlFilters);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]); // Only re-run when location changes

  const performSearch = (searchQuery, searchFilters) => {
    setLoading(true);
    
    const processedFilters = {
      ...searchFilters,
      minStat: searchFilters.minStat ? parseInt(searchFilters.minStat) : null,
      maxStat: searchFilters.maxStat ? parseInt(searchFilters.maxStat) : null
    };
    
    const searchResults = searchPokemon(searchQuery, processedFilters);
    setResults(searchResults);
    setLoading(false);
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      ability: '',
      minStat: '',
      maxStat: '',
      statType: 'hp'
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value && value !== 'hp');

  const breadcrumbItems = [
    { name: 'Search Pokemon' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50 py-8"
    >
      <SEO
        title="Search Pokemon - Advanced Pokemon Search & Filter"
        description="Search and filter Pokemon by name, type, abilities, stats, and more. Advanced Pokemon search with powerful filtering options."
        keywords="search pokemon, pokemon filter, find pokemon, pokemon search engine, advanced pokemon search"
        url="/search"
      />

      <div className="container mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        {/* Header */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Search Pokemon</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find Pokemon by name, type, abilities, or stats with our advanced search filters
          </p>
        </motion.div>

        {/* Search Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          {/* Main Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Pokemon by name..."
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                aria-label="Search Pokemon"
              />
              <SafeIcon 
                icon={FiSearch} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" 
                aria-hidden="true"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg border transition-colors focus:ring-2 focus:ring-blue-500 ${
                hasActiveFilters || showFilters
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiFilter} className="w-5 h-5" />
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="bg-white text-blue-600 rounded-full px-2 py-1 text-xs font-bold">
                  {Object.values(filters).filter(v => v && v !== 'hp').length}
                </span>
              )}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-200 pt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pokemon Type
                  </label>
                  <select
                    value={filters.type}
                    onChange={(e) => setFilters({...filters, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {pokemonTypes.map(type => (
                      <option key={type.name} value={type.name}>
                        {type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Ability Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ability
                  </label>
                  <select
                    value={filters.ability}
                    onChange={(e) => setFilters({...filters, ability: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Abilities</option>
                    {getAllAbilities().map(ability => (
                      <option key={ability} value={ability}>
                        {ability}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Stat Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stat Type
                  </label>
                  <select
                    value={filters.statType}
                    onChange={(e) => setFilters({...filters, statType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="hp">HP</option>
                    <option value="attack">Attack</option>
                    <option value="defense">Defense</option>
                    <option value="specialAttack">Sp. Attack</option>
                    <option value="specialDefense">Sp. Defense</option>
                    <option value="speed">Speed</option>
                  </select>
                </div>

                {/* Stat Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stat Range
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={filters.minStat}
                      onChange={(e) => setFilters({...filters, minStat: e.target.value})}
                      placeholder="Min"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="255"
                    />
                    <input
                      type="number"
                      value={filters.maxStat}
                      onChange={(e) => setFilters({...filters, maxStat: e.target.value})}
                      placeholder="Max"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="255"
                    />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors focus:ring-2 focus:ring-gray-500 rounded"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                    <span>Clear Filters</span>
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-6"
          >
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 mr-2">Active filters:</span>
              {filters.type && (
                <div className="flex items-center space-x-1">
                  <TypeBadge type={filters.type} size="sm" clickable={false} />
                </div>
              )}
              {filters.ability && (
                <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  {filters.ability}
                </span>
              )}
              {(filters.minStat || filters.maxStat) && (
                <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {filters.statType.charAt(0).toUpperCase() + filters.statType.slice(1)}: {filters.minStat || '0'}-{filters.maxStat || '255'}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Results */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">
              Search Results {!loading && `(${results.length} found)`}
            </h2>
          </div>

          <PokemonGrid pokemon={results} loading={loading} />

          {/* No Results */}
          {!loading && results.length === 0 && (query || hasActiveFilters) && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Pokemon Found</h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your search terms or filters to find more Pokemon.
              </p>
              <button
                onClick={() => {
                  setQuery('');
                  clearFilters();
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500"
              >
                Clear All Filters
              </button>
            </div>
          )}

          {/* Initial State */}
          {!loading && results.length === 0 && !query && !hasActiveFilters && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔎</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Start Your Search</h3>
              <p className="text-gray-600">
                Enter a Pokemon name or use filters to discover Pokemon that match your criteria.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default SearchPage;